import { useState, useEffect } from "react";
import { toast } from "sonner";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { DemoBadge } from "@/components/common";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Banknote, Loader2 } from "lucide-react";

export default function Funding() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product: "", name: "", email: "", phone: "", amount: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/funding/products").then((r) => setProducts(r.data)); }, []);

  const apply = (product) => { setForm({ ...form, product }); setOpen(true); };
  const submit = async () => {
    setBusy(true);
    try { await api.post("/funding/leads", form); toast.success("Application submitted"); setOpen(false); setForm({ product: "", name: "", email: "", phone: "", amount: "", notes: "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-11 h-11 rounded-lg bg-xd-blue/20 text-xd-blue"><Banknote size={22} /></span>
          <div><h1 className="font-heading text-4xl font-black tracking-tight">XD Funding</h1><p className="text-xd-muted">Capital and financing to fuel your growth.</p></div>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {products.map((p) => (
            <div key={p.id} data-testid={`funding-${p.id}`} className="bg-xd-paper border border-xd-border rounded-xl p-6 flex flex-col hover:border-xd-blue/50 transition-colors">
              <h3 className="font-heading text-lg font-bold">{p.name}</h3>
              <p className="mt-2 text-sm text-xd-muted flex-1">{p.description}</p>
              <p className="mt-3 text-xs text-xd-blue font-semibold">{p.terms}</p>
              <Button data-testid={`funding-apply-${p.id}`} onClick={() => apply(p.name)} className="mt-4 bg-xd-blue hover:bg-xd-blueH text-white" size="sm">Apply</Button>
            </div>
          ))}
        </div>

        <p className="mt-10 rounded-lg border border-xd-border bg-xd-paper p-4 text-xs text-xd-dim">
          Disclaimer: All funding products are provided by third-party lenders. Approvals are subject to third-party underwriting and eligibility criteria. XD FREIGHT does not guarantee approval and makes no guaranteed-approval claims.
        </p>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-xd-paper border-xd-border text-white">
          <DialogHeader><DialogTitle>Apply — {form.product}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xd-muted">Name</Label><Input data-testid="fund-name" value={form.name} onChange={set("name")} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div><Label className="text-xd-muted">Email</Label><Input data-testid="fund-email" type="email" value={form.email} onChange={set("email")} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div><Label className="text-xd-muted">Phone</Label><Input data-testid="fund-phone" value={form.phone} onChange={set("phone")} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div><Label className="text-xd-muted">Amount needed</Label><Input data-testid="fund-amount" value={form.amount} onChange={set("amount")} className="mt-1 bg-xd-surface border-xd-border" /></div>
            <div><Label className="text-xd-muted">Notes</Label><Textarea data-testid="fund-notes" value={form.notes} onChange={set("notes")} className="mt-1 bg-xd-surface border-xd-border" /></div>
          </div>
          <DialogFooter><Button data-testid="fund-submit" onClick={submit} disabled={busy} className="bg-xd-blue hover:bg-xd-blueH text-white">{busy ? <Loader2 className="animate-spin" size={16} /> : "Submit Application"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </PublicLayout>
  );
}
